import React, { useState, Suspense } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { QuizResult } from "../../shared/types";
import { cloudQuestions } from "./questions";
import UserInfoForm, { UserInfo } from "../../components/UserInfoForm";
import { webhookService } from "../../shared/webhookService";

// Explicitly import without lazy loading to ensure correct component
import CloudResults from "./CloudResults";

// Only lazy load the quiz container
const QuizContainer = React.lazy(
  () => import("../../components/Quiz/QuizContainer")
);

// Simple loading component
const ComponentLoader = ({ message }: { message: string }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
      color: "#667eea",
      fontSize: "1rem",
    }}
  >
    {message}
  </div>
);

const CloudQuiz: React.FC = () => {
  const [results, setResults] = useState<QuizResult[]>([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const navigate = useNavigate();

  const handleUserInfoSubmit = (userData: UserInfo) => {
    setUserInfo(userData);
    setShowQuiz(true);
  };

  const handleQuizComplete = async (quizResults: QuizResult[]) => {
    setResults(quizResults);

    // Prepare webhook data
    const webhookData = {
      quizName: "Cloud Quiz",
      userInfo: userInfo || {},
      responses: quizResults.map((result, index) => ({
        questionNumber: index + 1,
        question: result.question,
        answer: result.answer,
      })),
      timestamp: new Date().toISOString(),
    };

    // Send to webhook (with automatic retry on failure)
    await webhookService.submitQuizData(webhookData);

    // Navigate to results
    navigate("/cloud/results", { replace: true });
  };

  const handleRestart = () => {
    setResults([]);
    setShowQuiz(false);
    setUserInfo(null);
    navigate("/cloud", { replace: true });
  };

  return (
    <div>
      <Routes>
        <Route
          path="/"
          element={
            showQuiz ? (
              <Suspense
                fallback={
                  <ComponentLoader message="Loading Cloud Solutions Quiz..." />
                }
              >
                <QuizContainer
                  questions={cloudQuestions}
                  onQuizComplete={handleQuizComplete}
                />
              </Suspense>
            ) : (
              <UserInfoForm onSubmit={handleUserInfoSubmit} />
            )
          }
        />
        <Route
          path="/results"
          element={
            // Directly render CloudResults without lazy loading
            <CloudResults results={results} onRestart={handleRestart} />
          }
        />
      </Routes>
    </div>
  );
};

export default CloudQuiz;
